<template>
  <div class="space-y-1">
    <div
      v-for="giftCode in matchedGiftCodes"
      :key="giftCode.code"
      class="flex justify-between text-sm text-text"
    >
      <span
        >{{ $t('checkout.gift_card') }}:
        <span class="uppercase">{{ giftCode.code }}</span></span
      >
      <span
        >-{{
          formatPrice(giftCode.discount, giftCode.currency, $i18n.locale)
        }}</span
      >
    </div>

    <div class="flex justify-between text-sm text-text" v-if="totals.fee > 0">
      <span>{{ $t('common.fee') }}</span>
      <span>{{ formatPrice(totals.fee, 'DKK', $i18n.locale) }}</span>
    </div>
    <div class="flex justify-between text-sm text-text" v-if="totals.transaction_fee > 0">
      <span>{{ $t('common.transaction_fee') }}</span>
      <span>{{ formatPrice(totals.transaction_fee, 'DKK', $i18n.locale) }}</span>
    </div>
    <div class="flex justify-between text-sm text-text" v-if="totals.discount > 0">
      <span>{{ $t('common.discount') }}</span>
      <span>{{ formatPrice(-totals.discount, 'DKK', $i18n.locale) }}</span>
    </div>

    <div class="flex justify-between text-lg font-semibold">
      <span>{{ $t('common.total') }}</span>
      <span>{{ formatPrice(totals.total, 'DKK', $i18n.locale) }}</span>
    </div>
    <div class="flex justify-between text-sm text-text" v-if="totals.vat > 0">
      <span>{{ $t('common.vat') }}</span>
      <span>{{ formatPrice(totals.vat, 'DKK', $i18n.locale) }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed } from 'vue';

  import { useCartStore } from '@/stores/cart';
  import { formatPrice } from '@/utils/formatPrice';

  const cart = useCartStore();

  // No-op for backwards compatibility with callers that used to trigger a reload
  function loadCodes() {}
  defineExpose({ loadCodes });

  const matchedGiftCodes = computed(() => {
    // On receipt, use server-provided giftcard deductions
    if (cart.receiptCart) {
      const receiptGiftcards = cart.ReceiptConfirmPayment.giftcards?.length
        ? cart.ReceiptConfirmPayment.giftcards
        : cart.receiptOrder.giftcards;
      if (receiptGiftcards && receiptGiftcards.length > 0) {
        return receiptGiftcards.map(gc => ({
          code: gc.code,
          type: 'giftcard' as const,
          discount: Math.abs(gc.balance),
          currency: gc.currency,
        }));
      }
      return [];
    }

    // During checkout, cap each giftcard at remaining total
    const results: { code: string; type: 'giftcard'; discount: number; currency: string }[] = [];
    let remaining = cart.cartTotals.total + cart.cartTotals.total_giftcard_discount; // pre-deduction total
    for (const gc of cart.giftcards) {
      const deduction = Math.min(gc.balance, remaining);
      if (deduction > 0) {
        results.push({
          code: gc.code,
          type: 'giftcard',
          discount: deduction,
          currency: gc.currency,
        });
        remaining -= deduction;
      }
    }
    return results;
  });

  const totals = computed(() => {
    return {
      total: cart.cartTotals.total,
      fee: cart.cartTotals.total_ticketfee,
      vat: cart.cartTotals.total_vat,
      transaction_fee: cart.cartTotals.total_transaction_fee,
      discount: cart.cartTotals.total_discount,
      giftcard_discount: cart.cartTotals.total_giftcard_discount,
    };
  });
</script>
